import { Capacitor } from '@capacitor/core'
import { IonSpinner } from '@ionic/react'
import { $Enums, NewContract } from '@vae/server/dist/client'
import { ProductConsumptionCosts } from '@vae/server/src/modules/ExternalAPIs/Synerall/utils/costCalculators'
import { FixedProductDetails } from '@vae/server/src/types/synerallApiTypes'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { logEvent } from '../../../analytics/utils/firebaseUtils'
import Button from '../../../components/actions/Button'
import { decodeBase64 } from '../../../components/mainTabs/WebAppLayout'
import Stepper, { StepConfig } from '../../../components/stepper/Stepper'
import { ModalContext } from '../../../contexts/ModalContext'
import { UserContext } from '../../../contexts/UserContext'
import useUser from '../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import BillingMethodStep from '../../../modals/createContract/steps/BillingMethodStep'
import CustomerDetailsStep from '../../../modals/createContract/steps/CustomerDetailsStep'
import MoveOrChangeStep from '../../../modals/createContract/steps/MoveOrChangeStep'
import ProductDetailsStep from '../../../modals/createContract/steps/ProductDetailsStep'
import TermsAndConditionsStep from '../../../modals/createContract/steps/TermsAndConditionsStep'
import LockInDetailsStep from '../../../modals/createContract/steps/lockInDetailsStep/LockInDetailsStep'
import { trpc } from '../../../modules/trpc'
import { getMaxDate, getMinDate } from '../../../utils/dateUtils'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'
import MeteringPointInformationForm from './MeteringPointInformationForm'
import AdditionalDetailsStep from './steps/AdditionalDetailsStep'
import AuthenticateStep from './steps/AuthenticateStep'
import CurrentContractStep from './steps/CurrentContractStep'
import LastDayOfElecricityUseStep from './steps/LastDayOfElecricityUseStep'
import MoveoutTypeStep from './steps/MoveoutTypeStep'
import NewAddressStep from './steps/NewAddressStep'
import SelectOfferStep from './steps/SelectOfferStep'
import StartOfContractDateStep from './steps/StartOfContractDateStep'

export { AuthenticateStep }

export interface MoveOutFormFields {
	type: 'new_contract' | 'no_contract'
	newAddress: string
	newZipcode: string
	newPostalArea: string
	lastDayOfElectricityUse: string
	contractStartAt: string
	additionalDetails: string

	// New contract fields
	contract: NewContract
	selectedOffer: ProductConsumptionCosts
	currentContractGsrns: string[]
	isExistingCustomer: boolean
}

const MoveOutWizard: React.FC = () => {
	const { mutateAsync: createMoveout } = trpc.contact.createMoveout.useMutation<string>()

	const { insertModal } = useContext(ModalContext)
	const { setPostAuthRedirect, authExpire } = useContext(UserContext)
	const object = useContext(ObjectContext)

	const { t } = useTranslation('pages_contractConfirmation')
	const { t: tc } = useTranslation('modals_createContract')
	const { t: tcm } = useTranslation('modals_moveOutContact')

	const [isSending, setIsSending] = useState<boolean>(false)
	const [activeStep, setActiveStep] = useState(0)
	const [termsAccepted, setTermsAccepted] = useState<boolean>(false)

	const { user } = useUser()

	const form = useForm<MoveOutFormFields>({
		mode: 'onBlur',
		defaultValues: {
			type: 'new_contract',
			newAddress: '',
			newPostalArea: '',
			newZipcode: '',
			lastDayOfElectricityUse: getMinDate().format('YYYY-MM-DD'),
			contractStartAt: getMinDate().format('YYYY-MM-DD'),
			additionalDetails: '',
			contract: {
				id: undefined,
				contractStartDate: getMinDate().format('YYYY-MM-DD'),
				noAds: true,
				creationDate: dayjs().toISOString(),
				language: user?.language === $Enums.Language.en ? 'en' : 'fi',
				isLockIn: false,
				isContractUpdate: false,
				isMobile: true,
				invoiceType: 'email',
				productId: 0,
				productName: '',
			},
		},
	})

	const handleNext = async () => {
		logEvent(`click-moveout-next-step-${activeStep + 1}`)
		setActiveStep(prevActiveStep => prevActiveStep + 1)
	}

	const handlePrev = () => {
		logEvent(`click-moveout-previous-step-${activeStep - 1}`)
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}

	const { data: offers } = trpc.synerall.moveoutOffers.useQuery({ objectId: object.id })
	const { data: priceLockContracts } = trpc.synerall.priceLock.useQuery(
		{
			productId: form.watch('selectedOffer.product')?.ID ?? 0,
		},
		{ enabled: !!form.watch('selectedOffer.product')?.ID }
	)

	const onSelectOffer = (offer: ProductConsumptionCosts) => {
		form.setValue('selectedOffer', offer)

		if (offer.product) {
			const language = user?.language === $Enums.Language.en ? 'en' : 'fi'
			form.setValue('contract.productId', offer.product.ID)
			form.setValue(
				'contract.productName',
				offer.product?.Titles.find(x => x.Lang.toLowerCase() === language)?.Value ?? ''
			)
			handleNext()
		}
	}

	const { data: synerallUser } = trpc.synerall.userByPid.useQuery(
		{ personalId: user?.personalId ?? '' },
		{ enabled: !!user?.personalId }
	)

	useEffect(() => {
		if (synerallUser) {
			const email = synerallUser.Contacts.find(c => c.Type.toLowerCase() === 'email')?.Value
			const gsm = synerallUser.Contacts.find(c => c.Type.toLowerCase() === 'gsm')?.Value
			const phone = synerallUser.Contacts.find(c => c.Type.toLowerCase() === 'phone')?.Value

			form.setValue('contract', {
				...form.watch('contract'),
				ssn: synerallUser.PersonalId,
				email: email ?? '',
				phone: gsm ?? phone ?? '',
				firstName: synerallUser.FirstName,
				lastName: synerallUser.LastName,
			})
		}
	}, [synerallUser])

	const setLockIn = (contract: FixedProductDetails | null) => {
		const texts = user?.language === $Enums.Language.en ? contract?.texts.en : contract?.texts.fi
		const prices = user?.language === $Enums.Language.en ? contract?.prices.en : contract?.prices.fi

		if (contract) {
			form.setValue('contract', {
				...form.watch('contract'),
				isLockIn: true,
				lockInName: texts?.title ?? null,
				lockInPeriod: texts?.subtitle ?? null,
				lockInProductId: contract.productId ?? null,
				lockInHourlyPrice: prices?.hourlyPrice ?? null,
				lockInMonthlyPrice: prices?.monthlyPrice ?? null,
				lockInValidFrom: contract.validFrom ?? null,
				lockInValidTo: contract.validTo ?? null,
				lockInRatio: prices?.fixedRatio ?? null,
			})
		} else {
			form.setValue('contract', {
				...form.watch('contract'),
				isLockIn: false,
				lockInName: null,
				lockInPeriod: null,
				lockInProductId: null,
				lockInHourlyPrice: null,
				lockInMonthlyPrice: null,
				lockInValidFrom: null,
				lockInValidTo: null,
				lockInRatio: null,
			})
		}
	}

	const handleSubmit = async () => {
		const {
			type,
			lastDayOfElectricityUse,
			newAddress,
			newPostalArea,
			newZipcode,
			additionalDetails,
			contractStartAt,
			contract,
		} = form.getValues()
		setIsSending(true)

		if (process.env.react_app_environment !== 'LOCAL') {
			await createMoveout({
				objectId: object.id,
				type: type,
				lastDayOfElectricityUse: lastDayOfElectricityUse,
				contractStartAt: contractStartAt,
				newAddress: newAddress ?? '',
				newZipCode: newZipcode ?? '',
				newPostalArea: newPostalArea ?? '',
				additionalDetails: additionalDetails,
				newContract:
					type !== 'no_contract'
						? {
								...contract,
								contractStartDate: dayjs(contract.contractStartDate).format(
									'YYYY-MM-DDT00:00:00[Z]'
								),
							}
						: undefined,
			})
		}
		setIsSending(false)
		if (type === 'new_contract') {
			setPostAuthRedirect('contractConfirmation')
			insertModal('contractConfirmation')
		} else {
			setPostAuthRedirect('contractConfirmation')
			insertModal('contact-confirmation')
		}
	}

	const steps: StepConfig<MoveOutFormFields>[] = [
		{
			label: tcm('authenticateStep.authenticate'),
			key: 'AuthenticateStep',
			element: <AuthenticateStep redirectUri='/contact/move-out' />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled:
				Capacitor.isNativePlatform() ||
				(authExpire !== null && dayjs(decodeBase64(String(authExpire))).isAfter(dayjs())),
			disableNext: () => true,
			hideNext: true,
		},
		{
			label: tcm('moveoutTypeStep.title'),
			key: 'MoveoutTypeStep',
			element: <MoveoutTypeStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: false,
			disableNext: () => form.watch('type') === undefined || form.watch('type') === null,
		},
		{
			label: tcm('currentContract.title'),
			key: 'CurrentContractStep',
			element: <CurrentContractStep />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: form.watch('type') !== 'no_contract',
		},
		{
			label: tcm('newAddressStep.title'),
			key: 'NewAddressStep',
			element: <NewAddressStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: form.watch('type') !== 'no_contract',
			disableNext: () =>
				form.watch('newAddress') === undefined ||
				form.watch('newAddress') === null ||
				form.watch('newAddress') === '',
		},
		{
			label: tc('consumptionPointDetailsLabel'),
			key: 'consumptionPointDetails',
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			element: <MeteringPointInformationForm formState={form} nextSlide={handleNext} />,
			disabled: true,
			disableNext: () => !form.watch('contract.streetAddress'),
		},
		{
			label: tcm('lastDayOfElectricityStep.title'),
			key: 'LastDayOfElecricityUseStep',
			element: <LastDayOfElecricityUseStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: false,
			disableNext: () =>
				form.watch('lastDayOfElectricityUse') === undefined ||
				form.watch('lastDayOfElectricityUse') === null,
		},
		{
			label: tcm('startOfContractDateStep.title'),
			key: 'StartOfContractDateStep',
			element: <StartOfContractDateStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: true,
			disableNext: () =>
				form.watch('contractStartAt') === undefined || form.watch('contractStartAt') === null,
		},
		{
			label: tc('chooseProduct'),
			key: 'SelectOffer',
			element: offers ? <SelectOfferStep offers={offers} onSelectOffer={onSelectOffer} /> : <></>,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: form.watch('type') !== 'new_contract',
			disableNext: () =>
				form.watch('selectedOffer.product') === undefined ||
				form.watch('selectedOffer.product') === null,
		},
		{
			label: tc('productDetailsLabel'),
			key: 'productDetails',
			element: <ProductDetailsStep product={form.watch('selectedOffer.product')} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: form.watch('type') !== 'new_contract',
		},
		{
			label: tc('customerDetailsLabel'),
			key: 'customerDetails',
			element: (
				<CustomerDetailsStep
					contract={form.watch('contract')}
					setContract={(k, v) => form.setValue('contract', { ...form.watch('contract'), [k]: v })}
					nameNeeded={false}
					ssnNeeded={false}
				/>
			),
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: form.watch('type') !== 'new_contract',
			disableNext: () => !form.watch('contract.ssn'),
		},
		{
			label: tc('consumptionPointDetailsLabel'),
			key: 'consumptionPointDetails',
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			element: <MeteringPointInformationForm formState={form} nextSlide={handleNext} />,
			disabled: form.watch('type') !== 'new_contract',
			disableNext: () => !form.watch('contract.streetAddress'),
		},
		{
			label: tc('moveOrChangeLabel'),
			key: 'moveOrChange',
			ref: useRef<HTMLDivElement>(null),
			element: (
				<MoveOrChangeStep
					minDate={getMinDate().format('YYYY-MM-DD')}
					maxDate={getMaxDate().format('YYYY-MM-DD')}
					contract={form.watch('contract')}
					setContract={(k, v) => form.setValue('contract', { ...form.watch('contract'), [k]: v })}
				/>
			),
			animationMs: 200,
			disabled: form.watch('type') !== 'new_contract',
			disableNext: () => !form.watch('contract.contractStartDate'),
		},
		{
			label: tc('lockInDetailsLabel'),
			key: 'lockInDetails',
			element: (
				<div id='create-contract'>
					<div className='content-container'>
						<LockInDetailsStep
							contract={form.watch('contract')}
							setSelectedLockIn={setLockIn}
							lockInContracts={priceLockContracts
								?.filter(e => dayjs(e.validFrom).isBefore(dayjs().add(90, 'days')))
								.filter(e => dayjs(e.validFrom).isAfter(dayjs()))}
						/>
					</div>
				</div>
			),
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: !priceLockContracts?.length,
		},
		{
			label: tc('billingMethodLabel'),
			key: 'billingMethod',
			ref: useRef<HTMLDivElement>(null),
			element: (
				<BillingMethodStep
					contract={form.watch('contract')}
					setContract={(k, v) => form.setValue('contract', { ...form.watch('contract'), [k]: v })}
				/>
			),
			animationMs: 70,
			disabled: form.watch('type') !== 'new_contract',
			disableNext: () => !form.watch('contract.invoiceType'),
		},
		{
			label: tcm('additionalDetailsStep.title'),
			key: 'AdditionalDetailsStep',
			element: <AdditionalDetailsStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: false,
		},
		{
			label: tc('termsAndConditions'),
			key: 'termsAndConditions',
			ref: useRef<HTMLDivElement>(null),
			element: (
				<TermsAndConditionsStep
					contract={form.watch('contract')}
					setContract={(k, v) => form.setValue('contract', { ...form.watch('contract'), [k]: v })}
					termsAccepted={termsAccepted}
					setTermsAccepted={setTermsAccepted}
					offer={form.watch('selectedOffer')}
					onComplete={handleSubmit}
				/>
			),
			disabled: form.watch('type') !== 'new_contract',
			animationMs: 110,
		},
	]

	return (
		<PageWrapper page='moveOutWizard' appBarOptions={{ navigationType: 'back' }}>
			<form
				id='get-our-contract'
				onSubmit={handleSubmit}
				style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
			>
				<Stepper
					steps={steps}
					activeStep={activeStep}
					onNext={handleNext}
					onPrev={handlePrev}
					form={form}
					enableScrolling={false}
					lastStepAction={
						form.watch('type') !== 'new_contract' ? (
							<Button disabled={isSending} onClick={handleSubmit}>
								{isSending ? <IonSpinner /> : t('valmis', { ns: 'common' })}
							</Button>
						) : undefined
					}
				/>
			</form>
		</PageWrapper>
	)
}

export default MoveOutWizard
